import type { QuotaMetric } from '@examflow/api/contracts/quota';

interface Props {
  metric: QuotaMetric;
  reason: 'no_entitlement' | 'quota_exhausted';
  resetAt?: string;
}

const METRIC_LABELS: Record<QuotaMetric, string> = {
  ai_writing: 'AI 写作批改',
  mock_exam: '模考',
};

/**
 * UpgradePrompt — shown when a quota-gated action (AI 写作批改 / 模考)
 * is blocked with HTTP 402. Two flavours of reason:
 *  - no_entitlement: the user's current plan doesn't include this metric
 *  - quota_exhausted: the user has an entitlement but already used up the
 *    monthly allowance; the next reset date is shown when known.
 */
export function UpgradePrompt({ metric, reason, resetAt }: Props) {
  const label = METRIC_LABELS[metric];
  const title = reason === 'no_entitlement' ? `${label}需要会员权益` : `本月${label}次数已用完`;
  const description =
    reason === 'no_entitlement'
      ? '当前套餐不包含此项权益，升级套餐即可解锁。'
      : `次数将在 ${resetAt ? new Date(resetAt).toLocaleDateString() : '下月初'} 重置。`;

  return (
    <div
      className="my-4 rounded border border-amber-300 bg-amber-50 p-4"
      role="alert"
      aria-live="polite"
      data-testid="upgrade-prompt"
      data-metric={metric}
      data-reason={reason}
    >
      <h3 className="mb-1 font-semibold text-amber-900">{title}</h3>
      <p className="mb-3 text-sm text-gray-700">{description}</p>
      <div className="flex flex-wrap gap-2">
        <a
          href="/redeem"
          className="inline-block rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          兑换码
        </a>
      </div>
    </div>
  );
}

export default UpgradePrompt;
